import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { MenuItem } from "primereact/menuitem";
import { useCallback, useEffect, useState } from "react";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { Table } from "@velocity-ehs/component-library";
import { getOrgUnitLocations } from "../../supportFunctions/graphql/HierarchiesQueries";
import {
  getPagination,
  PaginationQueryObject,
  QuerySort,
  SortDirection,
} from "../../supportFunctions/graphql/Queries";
import { Location } from "../Locations/Location";
import { OrgUnit } from "./Hierarchies";
import { useTranslation } from "react-i18next";
import GatewayService from "../../../services/GatewayService";
interface LinkedLocationsTableProps {
  orgUnit?: OrgUnit;
  isEditMode: boolean;
  onUnlinkLocation?: (locationId: string) => void;
  isLoading: boolean;
}

const LinkedLocationsTable = ({
  orgUnit,
  isEditMode,
  onUnlinkLocation,
  isLoading,
}: LinkedLocationsTableProps): React.ReactElement => {
  const { showSnackbar } = useSnackbarActionContext();

  const { t } = useTranslation();

  const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number | undefined>(0);
  const [linkedLocations, setLinkedLocations] = useState<Location[]>([]);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 20,
    page: 0,
    sortField: "name",
    sortOrder: SortDirection.ASC,
    filters: {
      name: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
    },
  });

  const onChange = (event: any) => {
    setLazyParams(event);
  };

  const onLazyLoad = async () => {
    if (!orgUnit) return;
    setIsLoadingLocations(true);
    let querySort: QuerySort = null;
    if (lazyParams.sortField && lazyParams.sortOrder) {
      querySort = {
        sortField: lazyParams.sortField,
        sortDirection: lazyParams.sortOrder,
      } as QuerySort;
    }
    let paginationInput: PaginationQueryObject = getPagination(
      lazyParams.page + 1,
      lazyParams.rows,
      querySort
    );
    let filter = null;
    if (lazyParams.filters.name.value) {
      filter = {
        or: {
          name: lazyParams.filters.name.value,
        },
      };
    }

    try {
      const response = await GatewayService.api?.post(
        "",
        getOrgUnitLocations(orgUnit?.id, filter, paginationInput)
      );
      const locations = response?.data.data.organizationalUnit.locations;
      setTotalRecords(locations.pageInfo.totalElements);
      setLinkedLocations(locations.items);
    } catch (error: any) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const onGlobalSearch = (event: any): void => {
    const value = event.target.value;
    setLazyParams((state: any) => {
      return {
        ...state,
        filters: {
          name: {
            value,
            matchMode: FilterMatchMode.CONTAINS,
          },
        },
      };
    });
  };

  const onGlobalSearchCallback = useCallback((event: any) => {
    onGlobalSearch(event);
  }, []);

  const resetSearch = () => {
    onGlobalSearch({ target: { value: "" } });
  };

  const rowActions = (rowData: any): MenuItem[] => {
    if (!onUnlinkLocation) return [];
    return [
      {
        command: () => onUnlinkLocation(rowData.id),
        label: t("action_unlink"),
      },
    ];
  };

  useEffect(() => {
    onLazyLoad();
    // eslint-disable-next-line
  }, [lazyParams]);

  useEffect(() => {
    if (!isLoading && orgUnit) {
      const locations = orgUnit.locations;
      setLinkedLocations(locations.items);
      setTotalRecords(locations.pageInfo.totalElements);
      resetSearch();
    }
    // eslint-disable-next-line
  }, [isLoading, orgUnit]);

  return (
    <Table
      data={linkedLocations}
      params={lazyParams}
      totalRecords={totalRecords || 0}
      onPage={onChange}
      onSort={onChange}
      onGlobalSearch={onGlobalSearchCallback}
      isLoading={isLoading || isLoadingLocations}
      rowActions={isEditMode ? rowActions : undefined}
      fullPage={false}
    >
      <Column field="name" header={t("hierarchies_Location")} sortable />
      <Column field="lineage" header={t("hierarchies_Lineage")} sortable />
    </Table>
  );
};

export default LinkedLocationsTable;
